import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { CtaButtonComponent } from '../cta-button/cta-button.component';
import { IncomeService } from '../../services/income/income.service';
import { ScreenService } from '../../services/screen/screen.service';
import { ToastService } from '../../services/toast/toast.service';
import { IncomeSummary } from '../../models/income.model';
import { TEXT_CONSTANTS } from '../../constants/text-constants';

type IncomeModalView = 'loading' | 'with-history' | 'no-history' | 'change-value';

const SPANISH_MONTHS_SHORT = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];

/**
 * "Tu ingreso": muestra el ingreso resuelto (manual o promedio de los
 * últimos 3 meses) para splits de grupo "Según ingresos", y permite
 * configurarlo manualmente cuando no hay historial o el usuario quiere
 * cambiarlo (TAR-325, Req 2).
 */
@Component({
  selector: 'income-modal',
  standalone: true,
  imports: [CommonModule, FormsModule, CtaButtonComponent],
  templateUrl: './income-modal.component.html',
  styleUrl: './income-modal.component.css'
})
export class IncomeModalComponent implements OnInit, AfterViewInit, OnDestroy {
  @Output() close = new EventEmitter<void>();
  @Output() saved = new EventEmitter<IncomeSummary>();
  @ViewChild('amountInput') amountInput!: ElementRef<HTMLInputElement>;

  private _incomeService = inject(IncomeService);
  private _screenService = inject(ScreenService);
  private _toastService = inject(ToastService);

  TEXT = TEXT_CONSTANTS;

  view: IncomeModalView = 'loading';
  /** View to return to from 'change-value' when the user cancels. */
  private _previousView: IncomeModalView = 'no-history';

  summary: IncomeSummary | null = null;
  amountValue: number | null = null;
  isSaving = false;
  saveError: string | null = null;

  private _hostListener: ((event: KeyboardEvent) => void) | null = null;

  ngOnInit(): void {
    this.loadIncome();
  }

  ngAfterViewInit(): void {
    this._hostListener = (event: KeyboardEvent) => {
      if (event.key === 'Escape') this.onClose();
    };
    window.addEventListener('keydown', this._hostListener);
  }

  ngOnDestroy(): void {
    if (this._hostListener) window.removeEventListener('keydown', this._hostListener);
  }

  isMobileView(): boolean {
    return this._screenService.isMobile();
  }

  loadIncome(): void {
    this.view = 'loading';
    this._incomeService.getMyIncome().subscribe({
      next: (summary) => {
        this.summary = summary;
        if (summary.source === 'none') {
          this.view = 'no-history';
          this.amountValue = null;
          this._focusAmountInput();
        } else {
          this.view = 'with-history';
        }
      },
      error: () => {
        this.summary = { source: 'none', amount: null, currency: null, monthly_breakdown: [] };
        this.view = 'no-history';
        this._focusAmountInput();
      }
    });
  }

  private _focusAmountInput(): void {
    setTimeout(() => this.amountInput?.nativeElement?.focus(), 10);
  }

  get resolvedAmountFormatted(): string {
    if (!this.summary?.amount) return '';
    const currency = this.summary.currency || 'ARS';
    return `${currency} ${this.summary.amount.toLocaleString('es-AR', { maximumFractionDigits: 0 })}`;
  }

  get resolvedMessage(): string {
    const template = this.summary?.source === 'manual'
      ? this.TEXT.INCOME_MODAL.WITH_HISTORY.RESOLVED_MESSAGE_MANUAL
      : this.TEXT.INCOME_MODAL.WITH_HISTORY.RESOLVED_MESSAGE;
    return template.replace('{0}', this.resolvedAmountFormatted);
  }

  get monthlyBreakdownFormatted(): { label: string; amount: string }[] {
    if (!this.summary) return [];
    return this.summary.monthly_breakdown.map(m => ({
      label: `${SPANISH_MONTHS_SHORT[m.month - 1]}`,
      amount: `ARS ${m.total.toLocaleString('es-AR', { maximumFractionDigits: 0 })}`
    }));
  }

  onChangeValue(): void {
    this._previousView = 'with-history';
    this.amountValue = this.summary?.amount ?? null;
    this.saveError = null;
    this.view = 'change-value';
    this._focusAmountInput();
  }

  onConfirm(): void {
    this.close.emit();
  }

  onCancelChange(): void {
    this.saveError = null;
    this.view = this._previousView;
  }

  get isAmountValid(): boolean {
    return this.amountValue !== null && this.amountValue > 0;
  }

  onSave(): void {
    if (!this.isAmountValid) {
      this.saveError = this.TEXT.INCOME_MODAL.CHANGE_VALUE.ERROR_INVALID_AMOUNT;
      return;
    }

    this.isSaving = true;
    this.saveError = null;

    this._incomeService.setManualIncome(this.amountValue as number, this.summary?.currency || 'ARS').subscribe({
      next: () => {
        this.isSaving = false;
        this._toastService.success(this.TEXT.COMMON.SUCCESS, this.TEXT.INCOME_MODAL.SUCCESS);
        this.summary = {
          source: 'manual',
          amount: this.amountValue,
          currency: this.summary?.currency || 'ARS',
          monthly_breakdown: this.summary?.monthly_breakdown || [],
        };
        this.view = 'with-history';
        this.saved.emit(this.summary);
      },
      error: () => {
        this.isSaving = false;
        this.saveError = this.TEXT.INCOME_MODAL.CHANGE_VALUE.ERROR_SAVE;
      }
    });
  }

  onClose(): void {
    this.close.emit();
  }
}
